import { Component, EventEmitter, OnInit, ViewChild, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ActionBarGroup } from '@common/components/action-bar/action-bar.interface';
import { FormProperty } from '@common/components/app-control/app-control.component';
import { AppGridComponent } from '@common/components/app-grid/app-grid.component';
import { UploadActionButtonsComponent } from '@common/components/upload-action-buttons.component';
import { ColumnSettings } from '@common/interfaces/column-settings.interface';
import { GridSettings } from '@common/interfaces/grid-settings.interface';
import { User } from '@common/models/user';
import { ViewMode } from '@common/models/view-mode';
import { CommonService } from '@common/services/common.service';
import { DialogService } from '@common/services/dialog.service';
import { RememberStateService } from '@common/services/remember-state.service';
import { ToastrNotificationService } from '@common/services/toastr-notification.service';
import { environment } from '@environments/environment';
import { SignalQueryResult } from '@ngneat/query';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseResult, DialogSettings } from '@progress/kendo-angular-dialog';
import { GridComponent } from '@progress/kendo-angular-grid';
import { DrawerComponent } from '@progress/kendo-angular-layout';
import _ from 'lodash';
import { UserService } from '../services/user.service';
import { ExportType } from './export-type';

@Component({ template: '' })
export abstract class BaseListComponent<TQuery = any> implements OnInit {
    @ViewChild('drawer') drawer: DrawerComponent;
    protected toastrNotificationService: ToastrNotificationService;
    protected router: Router;
    protected userService: UserService;
    protected translateService: TranslateService;
    protected dialogService: DialogService;
    protected rememberStateService: RememberStateService;
    protected exportPDFCommand: string;
    protected exportExcelCommand: string;
    defaultViewMode = environment.settings.view.defaultViewMode;

    filtersChanged = new EventEmitter<any[]>();

    actionBar: ActionBarGroup[];
    @ViewChild(AppGridComponent) public appGrid: AppGridComponent;
    query: SignalQueryResult<TQuery, Error> = signal(null);
    user: User;
    isBusy = false;
    filterOpen = false;
    filter: any;
    createPermission: string;
    public selection = [];
    /**
     * @deprecated
     * Here until pages are refactored to use new NavigationService. This will be removed in the future.
     */
    breadcrumbs = null;
    /**
     * @deprecated
     * Here until pages are refactored to use new NavigationService. This will be removed in the future.
     */
    currentNavItem = null;

    parentRoute = window.location.pathname.split('/').slice(0, -1).join('/');
    abstract columns: ColumnSettings[];
    abstract queryName: string;
    persistFilter = environment.settings.list.persistFilter;
    private rememberFilterState = environment.settings.list.rememberFilterState;
    filterProperties: FormProperty[] = [];

    protected constructor(protected common: CommonService) {
        this.toastrNotificationService = common.toastrNotificationService;
        this.router = common.router;
        this.userService = common.userService;
        this.translateService = common.translateService;
        this.dialogService = common.dialogService;
        this.rememberStateService = common.rememberStateService;

        this.search = _.debounce(this.search.bind(this), 50);
        this.user = this.userService.currentUserSubject.getValue();

        if (this.rememberFilterState)
            this.filterOpen = this.rememberStateService.get<boolean>(`filterExpanded:${this.router.url}`);

        this.actionBar = [
            {
                label: 'Actions',
                items: [
                    {
                        label: 'New',
                        icon: 'faSolidPlus',
                        iconOnly: true,
                        variant: 'primary',
                        isVisible: () => this.canCreateNew(),
                        onClick: () => this.createNew()
                    }
                ]
            },
            {
                label: 'Export',
                items: [
                    {
                        label: 'Excel',
                        icon: 'faSolidFileExcel',
                        isVisible: () => this.canExportExcel(),
                        onClick: () => this.export(ExportType.Excel, false)
                    },
                    {
                        label: 'Pdf',
                        icon: 'faSolidFilePdf',
                        isVisible: () => this.canExportPDF(),
                        onClick: () => this.export(ExportType.Pdf)
                    }
                ]
            }
        ];
    }

    getDefaultFilter() {
        return {};
    }

    getCountryFlag(field: string) {
        return 'flag' + field.charAt(0).toUpperCase() + field.slice(1).toLowerCase();
    }

    /**
     * Function to get the base name of the query.
     * If the last character of the query name is 's', it removes the 's' from the end.
     * Then it splits the query name by capital letters.
     *
     * @returns The base name of the query, e.g. 'Users' -> 'User'
     */
    getBaseName(): string {
        const baseName =
            this.queryName[this.queryName.length - 1] === 's' ? this.queryName.slice(0, -1) : this.queryName;
        return baseName.replace(/([A-Z])/g, ' $1').trim();
    }

    ngOnInit() {
        this.initialize();
    }

    initialize() {
        this.filter = this.getFilter();
        this.search();
    }

    queryListData() {
        return this.common.queryService.getQuery<TQuery>(this.queryName, this.filter, {
            injector: this.common.injector
        });
    }

    prefetchListData(customFilter = null) {
        return this.common.queryService.prefetch(this.queryName, customFilter || this.filter);
    }

    search(customGrid = null) {
        this.selection = [];
        const grid = customGrid || (this.appGrid?.grid as GridComponent);
        const { pageSize, skip } = grid ?? { pageSize: 10, skip: 0 };
        if (!this.appGrid?.forceLocalSort) {
            const { field, dir } = (grid?.sort || { field: 'id', dir: 'desc' })?.[0] || {};
            this.filter = { ...this.filter, skip, take: pageSize, orderBy: field, orderDirection: dir };
        } else {
            this.filter = { ...this.filter, skip, take: pageSize };
        }
        this.saveFilter();
        this.query = this.queryListData().result;
        //Prefetch next page
        this.prefetchListData({ ...this.filter, skip: skip + pageSize, take: pageSize });
    }

    protected getFilter() {
        const persistedFilter = this.getPersistedFilter();
        const filter = this.getDefaultFilter();
        return this.persistFilter && _.isObject(filter) ? { ...filter, ...persistedFilter } : filter;
    }

    saveFilter() {
        this.rememberStateService.set(`filter:${this.router.url}`, JSON.stringify(this.filter));
    }

    clearFilter(name?: string) {
        this.common.queryService.queryClient.removeQueries({
            predicate: (query) => query.queryKey.some((key: any) => key === this.queryName)
        });

        if (name) {
            this.filter[name] = null;
            this.filterProperties.find((prop) => prop.name === name).initialValue = null;
        } else {
            this.filter = this.getDefaultFilter();
            this.filterProperties.forEach((prop) => (prop.initialValue = this.filter[prop.name]));
        }
        this.search();
    }

    async filterDialog(filterProperties: FormProperty[], dialogSettings: DialogSettings = { width: '50%' }) {
        const data = await this.dialogService.form({
            options: {
                title: 'Filter',
                cancelAction: () => this.clearFilter(),
                getDefaultFilter: () => this.getDefaultFilter(),
                cancelText: 'Clear',
                cancelIcon: 'faSolidFilterCircleXmark',
                confirmText: 'Filter',
                confirmIcon: 'faSolidFilter',
                model: { ...this.filter },
                properties: filterProperties
            },
            dialogSettings
        });
        if (data instanceof DialogCloseResult || typeof data !== 'object') return;
        this.filter = data;
        filterProperties.forEach((prop) => (prop.initialValue = this.filter[prop.name]));
        this.search();
        this.resetPaging();
    }

    private resetPaging() {
        this.appGrid.onDataStateChanged({
            skip: 0,
            take: this.appGrid?.grid?.pageSize || environment.settings.grid.pageSize
        });
    }

    toggleFilter() {
        this.filterOpen = !this.filterOpen;
    }

    protected getPersistedFilter() {
        if (this.persistFilter === false) return {};

        try {
            const filter = this.rememberStateService.get<string>(`filter:${this.router.url}`);
            return JSON.parse(filter);
        } catch (error) {
            return {};
        }
    }

    canCreateNew() {
        if (this.createPermission) return this.user?.hasPermission(this.createPermission);
        return true;
    }

    createNew() {
        this.router.navigate([`${this.parentRoute}/create/`]);
    }

    actionHasPermission(permission) {
        return this.user?.hasPermission(permission);
    }

    public canExportPDF() {
        return !!this.exportPDFCommand;
    }

    public canExportExcel() {
        return !!this.exportExcelCommand;
    }

    async export(exportType: ExportType, allData = false, customGrid = null) {
        const grid = customGrid || (this.appGrid.grid as GridComponent);
        const data: any = {
            columnDefs: grid.columns
                .toArray()
                .filter((x: any) => x.field)
                .map((x: any) => ({
                    header: x.displayTitle,
                    field: x.field
                })),
            exportType,
            query: this.filter ?? {},
            selectedIds: this.selection
        };
        if (allData === true) data.query.skip = data.query.take = 0;

        const exportMutation = this.common.queryService.getCommandMutation();
        return exportMutation
            .mutateAsync({
                command: exportType === ExportType.Pdf ? this.exportPDFCommand : this.exportExcelCommand,
                data
            })
            .then((res: any) => res && UploadActionButtonsComponent.download(res.fileName, res.content));
    }

    saveGridSettings(settings?: { value: GridSettings; id?: number }) {
        const saveModel = { key: this.router.url, value: JSON.stringify(settings.value) };

        this.common.queryService
            .getCommandMutation()
            .mutateAsync({
                command: 'SaveGridSetting',
                data: { saveModel, id: settings?.id, action: settings?.id ? ViewMode.edit : ViewMode.create },
                invalidate: 'gridSettings'
            })
            .catch((err) => {
                this.toastrNotificationService.show({ type: 'error', message: 'Error saving grid settings' });
                return err;
            });
    }

    deleteGridSettings() {
        this.common.queryService
            .getCommandMutation()
            .mutateAsync({
                command: 'DeleteGridSetting',
                data: { settingKey: this.router.url },
                invalidate: 'gridSettings'
            })
            .catch((err) => {
                this.toastrNotificationService.show({ type: 'error', message: 'Error clearing grid settings' });
                return err;
            });
    }

    get nrOfActiveFilters(): number {
        const clone = _.clone(this.getFilter());
        delete clone.skip;
        delete clone.take;
        delete clone.orderBy;
        delete clone.orderDirection;
        return Object.values(clone).filter((x) => ![null, undefined].includes(x))?.length || 0;
    }

    get listOfActiveFilters(): { label: string; value: string }[] {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { skip, take, orderBy, orderDirection, ...filters } = this.getFilter();
        return (
            Object.entries(filters)
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                ?.filter(([label, value]) => value)
                .map(([label, value]) => ({ label, value: value?.toString() }))
        );
    }

    onSelectedKeysChange(keys: any[]) {
        this.selection = keys;
        this.actionBar = [...this.actionBar];
    }

    executeCommand(
        command: string,
        data = {},
        thenAction: (item?: any) => void = () =>
            this.toastrNotificationService.show({ type: 'success', message: 'Success' })
    ): void {
        const ids = this.appGrid.selectBy === 'row' ? this.selection.map((x) => x.id) : this.selection;
        this.common.queryService
            .getCommandMutation()
            .mutateAsync({ command, data: { ids, ...data }, invalidate: this.queryName })
            .then(thenAction);
    }
}
